import { CommonModule } from '@angular/common';
import { Component, OnInit, ViewEncapsulation, OnDestroy } from '@angular/core';
import { firstValueFrom, Subscription } from 'rxjs';
import { applicationService } from 'src/app/services/applications.service';
import { NotificationsService } from 'src/app/services/notifications/notifications.service';
import { FormsModule, ReactiveFormsModule, FormBuilder, FormGroup, FormControl, Validators, FormArray } from '@angular/forms';
import { SigningPadComponent } from 'src/app/shared/signature/signature.component';
import { ActivatedRoute, Router, RouterModule } from '@angular/router';
import { phoneValidator } from 'src/app/shared/helper/password.validator';
import { applicationType } from 'src/app/admissions/interface';
import { RecaptchaV3Service } from 'src/app/services/recaptcha-v3.service';
import { RecaptchaService } from 'src/app/services/recaptcha.service';
import { environment } from 'src/environments/environment';
import { MatSnackBar, MatSnackBarModule } from '@angular/material/snack-bar';
import { MatIcon } from '@angular/material/icon';

interface ApplicationConfig {
  type: applicationType;
  displayName: string;
  noApplicationText: string;
  submitButtonText: string;
  successMessage: string;
  containerClass: string;
}

@Component({
  selector: 'app-application-dynaimc-form',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    SigningPadComponent,
    RouterModule,
    MatSnackBarModule,
    MatIcon
  ],
  templateUrl: './application-dynamic-form.html',
  styleUrls: ['./application-dynamic-form.scss'],
  encapsulation: ViewEncapsulation.None
})
export class ApplicationDynamicFormComponent implements OnInit, OnDestroy {
  // Main data properties
  application: any;
  sections: any[] = [];
  form: FormGroup;
  
  // State properties
  adminView: boolean = false;
  isApplicationMade: boolean = false;
  haveApplication: boolean = true;
  isLoading: boolean = true;
  isSubmitting: boolean = false;
  isPublicRoute: boolean = false;
  isEmbedMode: boolean = false;
  embedApplicationId: string | null = null;
  formSubmitted: boolean = false; // Add this flag
  
  // Query params
  token: string | null = null;
  lead_id: string | null = null;
  showLogo: boolean = false;
  logoUrl: string = '';
  
  // Configuration based on application type
  config: ApplicationConfig;
  applicationType = applicationType;
  applicationId:any = null
  isPreviewMode: boolean = false;
  
  // For tracking subscriptions
  private routeSub: Subscription | null = null;

  // Define configurations for different application types
  private readonly configs: Record<string, ApplicationConfig> = {
    [applicationType.lead_application]: {
      type: applicationType.lead_application,
      displayName: 'Application',
      noApplicationText: 'No application available',
      submitButtonText: 'Submit Application',
      successMessage: 'Application submitted successfully!',
      containerClass: 'application-container'
    },
    [applicationType.financial_aid]: {
      type: applicationType.financial_aid,
      displayName: 'Financial Aid Application',
      noApplicationText: 'No Financial Aid Application Available',
      submitButtonText: 'Submit Financial Aid Application',
      successMessage: 'Financial aid application submitted successfully!',
      containerClass: 'financial-aid-container'
    }
  };

  constructor(
    private applicationService: applicationService,
    private notificationService: NotificationsService,
    private fb: FormBuilder,
    private route: ActivatedRoute,
    private router: Router,
    private recaptchaV3Service: RecaptchaV3Service,
    private recaptchaService: RecaptchaService,
    private snackBar: MatSnackBar
  ) {
    this.form = this.fb.group({});
    
    // Default configuration
    this.config = this.configs[applicationType.lead_application];
  }

  async ngOnInit(): Promise<void> {
    // Check if we're in embed mode
    const routeData = this.route.snapshot.data;
    this.isEmbedMode = routeData['isEmbed'] === true;
    this.isPreviewMode = this.route.snapshot.queryParamMap.get('preview') === 'true';

    
    if (this.isEmbedMode) {
      // Get application ID from route for embed mode
      this.embedApplicationId = this.route.snapshot.paramMap.get('id');
      
      // Set config based on route data
      const embedType = routeData['type'];
      if (embedType && this.configs[embedType]) {
        this.config = this.configs[embedType];
      }
      
      // Load embed-specific data
      await this.loadEmbedData();
    } else {
      // Existing route handling
      this.routeSub = this.route.paramMap.subscribe(params => {
        this.adminView = params.get('isAdmin') === 'true';
        this.lead_id = params.get('lead_id');
        
        // Determine application type from route
        const routeType = params.get('type');
        if (routeType && this.configs[routeType]) {
          this.config = this.configs[routeType];
        } else {
          // Check route URL to determine type
          const url = this.router.url;
          if (url.includes('financial-aid')) {
            this.config = this.configs[applicationType.financial_aid];
          }
        }
      });
      
      // Get the token from query params
      this.token = this.route.snapshot.queryParamMap.get('token');
      this.isPublicRoute = !!this.token && !this.adminView;

      // Pre-load reCAPTCHA for public routes only
      if (this.isPublicRoute) {
        try {
          await this.recaptchaV3Service.execute('page_load');
        } catch (error) {
          console.error('Failed to initialize reCAPTCHA:', error);
        }
      }

      // Load application data
      this.loadData();
    }
  }

  ngOnDestroy(): void {
    // Clean up subscriptions
    if (this.routeSub) {
      this.routeSub.unsubscribe();
    }
  }

  /**
   * Check if a field is invalid and should show error
   * Only shows error if field is touched or form was submitted
   */
  isFieldInvalid(fieldName: string): boolean {
    const field = this.form.get(fieldName);
    return !!(field && field.invalid && (field.touched || this.formSubmitted));
  }
  
  async loadEmbedData(): Promise<void> {
    this.isLoading = true;
    
    try {
      // Debug: Log the embed application ID
      
      const queryParam = {
        id: this.embedApplicationId,
        isEmbed: true,
        typeApplication: this.config.type
      };
  
      // Debug: Log the query parameters
  
      const response = await firstValueFrom(
        this.applicationService.getApplicationEmbed(queryParam)
      );
  
      // Debug: Log the response
  
      const { application } = response;
  
      if (application?.showLogo) {
        const companyLogo = application?.Company?.companyLogo;
        this.showLogo = true;
        this.logoUrl = companyLogo ? 
          `${environment.publicCloudFlareUrl}/company-logo/thumb_${companyLogo}` : '';
      }
  
      // Extract and parse sections
      const { sections, ...rest } = application;
      try {
        this.sections = JSON.parse(sections);
        this.buildForm();
      } catch (parseError) {
        this.sections = [];
        console.error('Failed to parse sections:', parseError);
      }
      
      this.application = rest;
      this.haveApplication = true;
    } catch (error: any) {
      this.haveApplication = false;
      console.error('Failed to load embed application:', error);
      // Add more specific error handling
      if (error.message && error.message.includes('Invalid URL')) {
        console.error('Invalid URL error. Check your environment.apiUrl configuration.');
        console.error('Current apiUrl:', environment.apiUrl);
      }
    } finally {
      this.isLoading = false;
    }
  }

  /**
   * Loads application data and configuration
   */
  async loadData(): Promise<void> {
    let formSubmitted: any;
    this.isLoading = true;
    
    try {
      // Prepare query parameters based on context
      let queryParam: any = { active: true, default: true };

      if (this.adminView === true) {
        queryParam = {
          ...queryParam,
          lead_id: this.lead_id,
          isAdminView: true,
          typeApplication: this.config.type
        };
      } else if (this.token) {
        queryParam = {
          ...queryParam,
          token: this.token,
          lead_id: this.lead_id,
          typeApplication: this.config.type
        };
      } else {
        queryParam = {
          ...queryParam,
          isAdminView: false,
          typeApplication: this.config.type
        };
      }

      // Get application data from service
      const { application, hasApplication } = await firstValueFrom(
        this.applicationService.getApplication(queryParam, this.token)
      );

      // Check if application was already submitted
      if (hasApplication) {
        this.isApplicationMade = true;
        this.applicationId = hasApplication.id
        formSubmitted = JSON.parse(hasApplication?.data || null);
      }

      if(application?.showLogo){
        const companyLogo = application?.Company?.companyLogo
        this.showLogo = true
        this.logoUrl = companyLogo ? `${environment.publicCloudFlareUrl}/company-logo/thumb_${companyLogo}` : '';
      }

      // Extract sections and application data
      const { sections, ...rest } = application;
      try {
        this.sections = JSON.parse(sections);
        this.buildForm(formSubmitted ? formSubmitted : null);
        
        // Disable form in admin view or if already submitted
        if (this.adminView || this.isApplicationMade) {
          this.form.disable();
        }
      } catch (parseError) {
        this.sections = [];
        console.error('Failed to parse sections:', parseError);
        this.notificationService.showClientError('Failed to parse application structure.');
      }
      
      this.application = rest;
    } catch (error: any) {
      this.haveApplication = false;
      this.notificationService.showClientError(
        error?.error?.message || `Unable to load ${this.config.displayName.toLowerCase()}.`
      );
    } finally {
      this.isLoading = false;
    }
  }

  /**
   * Generate a unique form control key using section and question indices.
   * This prevents collisions when multiple questions share the same name
   * (e.g. "First Name" in Admission, Student Signature, and Parent Signature).
   */
  getFormControlKey(sectionIndex: number, questionIndex: number, name: string): string {
    const cleanName = name.replace(/\s+/g, '_').replace(/[^a-zA-Z0-9_]/g, '');
    return `s${sectionIndex}_q${questionIndex}_${cleanName}`;
  }

  buildForm(existingData: any = null): void {
    const group: { [key: string]: any } = {};
    
    this.sections.forEach((section: any, sIdx: number) => {
      section.questions.forEach((question: any, qIdx: number) => {

        if (question.type === 'section') return;

        const key = this.getFormControlKey(sIdx, qIdx, question.name);

        // Support both new indexed keys and legacy keys (original question name)
        // so previously submitted applications still display correctly.
        const existingValue = existingData != null
          ? (existingData[key] ?? existingData[question.name] ?? null)
          : null;
        
        // Handle checkbox groups differently
        if (question.type === 'checkbox') {
          const controls = question.items.map((item: any, index: number) => {
            const value = existingValue ? existingValue[index] : false;
            return new FormControl({ value, disabled: !!existingData });
          });
          group[key] = this.fb.array(controls);
        } 
        // Handle signature fields
        else if (question.type === 'signature') {
          group[key] = new FormControl(
            { value: existingValue ?? '', disabled: !!existingData },
            question.required ? [Validators.required] : []
          );
        } 
        // Handle email fields with email validation
        else if (question.type === 'email') {
          group[key] = new FormControl(
            { value: existingValue ?? '', disabled: !!existingData },
            question.required ? [Validators.required, Validators.email] : [Validators.email]
          );
        }
        // Handle phone fields with custom validator
        else if (question.type === 'phone') {
          group[key] = new FormControl(
            { value: existingValue ?? '', disabled: !!existingData },
            {
              validators: question.required ? [Validators.required, phoneValidator()] : [phoneValidator()],
              updateOn: 'blur'
            }
          );
        }
        // Handle terms checkboxes that require true value
        else if (question.type === 'terms') {
          group[key] = new FormControl(
            { value: existingValue ?? false, disabled: !!existingData },
            question.required ? [Validators.requiredTrue] : []
          );
        }
        // Handle all other field types
        else {
          group[key] = new FormControl(
            { value: existingValue ?? '', disabled: !!existingData },
            question.required ? [Validators.required] : []
          );
        }
      });
    });
  
    this.form = this.fb.group(group);
  }

  /**
   * Handles form submission
   */
  async onSubmit(): Promise<void> {
    // Set formSubmitted flag to true
    if (this.isSubmitting) return;  // ← Add this guard

    this.formSubmitted = true;

    // Check if form fields are valid
    if (this.isPreviewMode) {
      this.notificationService.showNonErrorSnackBar('This is preview mode. Submission is disabled.');
      return;
    }

    if (this.form.valid) {
      this.isSubmitting = true;

      try {
        if (this.isEmbedMode) {
          // For embed mode, submit directly without reCAPTCHA
          await this.submitEmbedForm();
        } else {
          // Try to submit with v3 first
          await this.submitWithRecaptcha('v3');
        }
      } catch (error: any) {
        console.error('Submission failed:', error);

        if(error?.error?.status == 409 && error?.error?.message.includes('has already been submitted')){
          this.snackBar.open(error?.error?.message, 'OK', {
            duration: 2000
          });
        }
        
        // If v3 failed and not embed mode, try v2
        else if (!this.isEmbedMode && this.isPublicRoute && !this.isApplicationMade) {
          this.snackBar.open('Additional verification required', 'OK', {
            duration: 2000
          });

          try {
            const v2Token = await this.recaptchaService.showCaptchaModal({
              autoClose: true,
              autoCloseDelay: 500
            });

            if (v2Token) {
              await this.submitWithRecaptcha('v2', v2Token);
            } else {
              this.snackBar.open('Verification cancelled', 'OK', {
                duration: 3000,
                panelClass: 'error-snackbar'
              });
            }
          } catch (v2Error) {
            console.error('v2 submission also failed:', v2Error);
            this.notificationService.showClientError(
              'Security verification failed. Please try again.'
            );
          }
        } else {
          this.notificationService.showClientError(
            error?.error?.message || `Failed to submit ${this.config.displayName.toLowerCase()}.`
          );
        }
      } finally {
        this.isSubmitting = false;
      }
    } else {
      // Don't mark form as touched here - the formSubmitted flag will handle showing errors
      this.notificationService.showClientError('Please fill out all required fields.');
    }
  }

  /**
   * Submit form for embed mode
   */
  private async submitEmbedForm(): Promise<void> {
    const formData = this.getSubmittableFormData(this.form.value);

    const data: any = { 
      data: JSON.stringify(formData),
      application_id: this.application.id,
      type: this.config.type,
      isEmbed: true,
      isPreview: this.isPreviewMode // Add this flag
    };

    await firstValueFrom(
      this.applicationService.submitEmbedApplication(data)
    );

    this.notificationService.showNonErrorSnackBar(this.config.successMessage);
    this.form.disable();
    this.isApplicationMade = true;
    this.formSubmitted = false; // Reset after successful submission
  }

  /**
   * Submit form with reCAPTCHA
   */
  private async submitWithRecaptcha(version: 'v3' | 'v2', providedToken?: string): Promise<void> {
    let captchaToken: string | null = null;

    // Get captcha token for public routes
    if (this.isPublicRoute && !this.isApplicationMade) {
      if (version === 'v3') {
        // Execute reCAPTCHA v3 with action based on application type
        const action = this.config.type === applicationType.financial_aid ? 
          'submit_financial_aid' : 'submit_application';
        
        captchaToken = await this.recaptchaV3Service.execute(action);
      } else {
        captchaToken = providedToken || null;
      }

      if (!captchaToken) {
        throw new Error('No captcha token available');
      }
    }

    // Map cleaned form control names back to original names
    const formData = this.getSubmittableFormData(this.form.value);

    const data: any = { 
      data: JSON.stringify(formData),
      application_id: this.application.id,
      type: this.config.type,
    };

    // Add the captcha token and action if available
    if (captchaToken) {
      data.recaptchaToken = captchaToken;
      data.recaptchaAction = this.config.type === applicationType.financial_aid ? 
        'submit_financial_aid' : 'submit_application';
      data.recaptchaVersion = version;
    }
    
    if (this.token) {
      await firstValueFrom(this.applicationService.submitApplicationToken(data, this.token));
    } else {
      await firstValueFrom(this.applicationService.submitApplication(data));
    }

    this.notificationService.showNonErrorSnackBar(this.config.successMessage);
    
    // Disable the form after successful submission
    this.form.disable();
    this.isApplicationMade = true;
    this.formSubmitted = false; // Reset after successful submission
  }

  /**
   * Map form data from unique keys back to original question names.
   * Uses the same section/question indices to reconstruct the submitted data
   * with original question names as keys.
   */
  private getSubmittableFormData(formData: any): any {
    const mappedData: any = {};
    
    this.sections.forEach((section: any, sIdx: number) => {
      section.questions.forEach((question: any, qIdx: number) => {
        if (question.type === 'section') return;
        const key = this.getFormControlKey(sIdx, qIdx, question.name);
        if (formData[key] !== undefined) {
          mappedData[key] = formData[key];
        }
      });
    });
    
    return mappedData;
  }

  /**
   * Marks all controls in the form group as touched to trigger validation
   * @param formGroup - The form group to mark as touched
   */
  private markFormGroupTouched(formGroup: FormGroup): void {
    Object.values(formGroup.controls).forEach(control => {
      control.markAsTouched();
      
      if (control instanceof FormGroup) {
        this.markFormGroupTouched(control);
      } else if (control instanceof FormArray) {
        control.controls.forEach((ctrl: any) => {
          if (ctrl instanceof FormGroup) {
            this.markFormGroupTouched(ctrl);
          } else {
            ctrl.markAsTouched();
          }
        });
      }
    });
  }

  /**
   * Get the value for display (handles checkbox arrays)
   */
  getDisplayValue(question: any, sectionIndex: number, questionIndex: number): any {
    const key = this.getFormControlKey(sectionIndex, questionIndex, question.name);
    const control = this.form.get(key);
    
    if (question.type === 'checkbox' && control instanceof FormArray) {
      // Return array of checked items
      return question.items
        .map((item: any, index: number) => control.at(index)?.value ? item.value : null)
        .filter((value: any) => value !== null)
        .join(', ') || 'None selected';
    }
    
    return control?.value || '';
  }

  /**
   * Exports application form to PDF
   */
  async exportApplication(): Promise<void> {
    try {
      this.notificationService.showNonErrorSnackBar('Generating PDF...', 1000);

      if(!this.applicationId){
        this.notificationService.showClientError('No application to generate...');
        return;
      }
      
      // Use the same service method as documents component
      const blob = await firstValueFrom(
        this.applicationService.exportApplicationPdf(this.applicationId)
      );
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Generate filename
      const filePrefix = this.config.type === applicationType.financial_aid ? 'Financial Aid' : 'Admission Application';
      const fileName = `${filePrefix}_${this.lead_id}_${new Date().getTime()}.pdf`;
      link.download = fileName;
      
      link.click();
      window.URL.revokeObjectURL(url);
      
      this.notificationService.showNonErrorSnackBar('PDF downloaded successfully', 2000);
    } catch (error) {
      console.error('Error exporting application:', error);
      this.notificationService.showClientError('Failed to export application. Please try again.');
    }
  }
}